/**
 * WhatsNewCard — the default (non-experience) What's New card, shell included.
 *
 * Shared deliberately: the student dialog and the admin preview render THIS
 * component, so a preview cannot drift from what a user actually sees.
 *
 * The tip's title is the card's only heading — it is the modal's header bar,
 * not a line inside the body. With no title there is no header, so the card is
 * pure content (a full-bleed image, typically) and a floating close button
 * stands in for the header's ✕.
 */

import type { ReactNode } from 'react';
import { Modal } from '../components/Modal';
import './WhatsNewDialog.css';

export type ImageFit = 'contain' | 'full-bleed';

export interface WhatsNewCardProps {
    isOpen: boolean;
    title?: string;
    body?: string;
    imageUrl?: string;
    /** Both scale the whole image into the space the dialog gives it — neither
     *  crops. 'contain' keeps the body padding and rounded corners around it;
     *  'full-bleed' runs it to the dialog's edges. */
    imageFit?: ImageFit;
    onClose: () => void;
    footer?: ReactNode;
    closeOnOverlayClick?: boolean;
    closeOnEscape?: boolean;
}

export function WhatsNewCard({
    isOpen,
    title,
    body,
    imageUrl,
    imageFit = 'contain',
    onClose,
    footer,
    closeOnOverlayClick = true,
    closeOnEscape = true,
}: WhatsNewCardProps) {
    const heading = title?.trim() || undefined;
    const bleed = imageFit === 'full-bleed' && !!imageUrl;
    // Nothing on the card but the artwork and the floating ✕. The stable height
    // below exists to stop cards jumping size as you page between them, but with
    // no text to share the box the image just letterboxes inside it — bands of
    // dialog above and below. With one thing to show, the image IS the card, so
    // the dialog takes its shape instead.
    // Full-bleed only: 'contain' is a request for the padded, rounded frame, and
    // that frame is exactly what this drops.
    const imageOnly = bleed && !heading && !body?.trim() && !footer;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={heading}
            size="md"
            // An image card gets a stable dialog height (see .wn-card-modal) so
            // cards don't jump size between tips; a text-only card sizes to its
            // content as any other modal does.
            className={imageUrl ? (imageOnly ? 'wn-card-image-only' : 'wn-card-modal') : undefined}
            closeOnOverlayClick={closeOnOverlayClick}
            closeOnEscape={closeOnEscape}
            footer={footer}
        >
            <div className={`wn-tip${imageUrl ? ' wn-tip-has-image' : ''}${bleed ? ' wn-tip-bleed' : ''}${heading ? '' : ' wn-tip-headless'}${imageOnly ? ' wn-tip-image-only' : ''}`}>
                {!heading && (
                    <button className="wn-tip-close" onClick={onClose} aria-label="Close">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                )}
                {imageUrl && (
                    <img
                        className={`wn-tip-image${bleed ? ' wn-tip-image-bleed' : ''}`}
                        src={imageUrl}
                        alt={heading ?? ''}
                    />
                )}
                {body && <div className="wn-tip-body">{body}</div>}
            </div>
        </Modal>
    );
}
